import React, { useState, useEffect, useRef } from 'react';
import { GameManager } from './core/GameManager';
import {
  GameState,
  Player,
  Card,
  AvailableActions,
  ActionType,
  ChiOption,
  WinResult,
  GameConfig,
  BotDifficulty
} from './core/types';
import GameBoard from './ui/GameBoard';
import { soundManager } from './utils/sound';
import { DEFAULT_RULE_CONFIG } from './core/rules.config';
import './App.css';

const App: React.FC = () => {
  const [gameManager, setGameManager] = useState<GameManager | null>(null);
  const [gameStarted, setGameStarted] = useState<boolean>(false);
  const [gameState, setGameState] = useState<GameState>(GameState.IDLE);
  const [players, setPlayers] = useState<Player[]>([]);
  const [publicCard, setPublicCard] = useState<Card | null>(null);
  const [isPublicCardDrawn, setIsPublicCardDrawn] = useState<boolean>(false);
  const [currentTurnPlayer, setCurrentTurnPlayer] = useState<Player | null>(null);
  const [dealerPlayer, setDealerPlayer] = useState<Player | null>(null);
  const [deckSize, setDeckSize] = useState<number>(0);
  const [currentRound, setCurrentRound] = useState<number>(1);
  const [totalRounds, setTotalRounds] = useState<number>(4);
  const [winResult, setWinResult] = useState<WinResult | null>(null);
  const [humanActions, setHumanActions] = useState<AvailableActions | null>(null);
  const [botBanters, setBotBanters] = useState<Record<string, string>>({});
  const banterTimers = useRef<Record<string, any>>({});

  const [config, setConfig] = useState<GameConfig>({
    playerCount: 3,
    handSize: 21,
    minHuxiToWin: DEFAULT_RULE_CONFIG.minHuxiToWin,
    rules: DEFAULT_RULE_CONFIG,
    totalRounds: 4,
    botDifficulty: BotDifficulty.MEDIUM,
    soundEnabled: true,
    gameSpeed: 'normal'
  });

  // 初始化游戏管理器
  useEffect(() => {
    const manager = new GameManager(config);

    manager.subscribe((event: any) => {
      if (event.type === 'STATE_CHANGED') {
        const data = event.data;
        setGameState(data.gameState);
        setPlayers([...data.players]);
        setPublicCard(data.publicCard);
        setIsPublicCardDrawn(data.isPublicCardDrawn);
        setCurrentTurnPlayer(data.currentTurnPlayer);
        setDealerPlayer(data.dealerPlayer);
        setDeckSize(data.deckSize);
        setCurrentRound(data.currentRound);
        setTotalRounds(data.totalRounds);
        setWinResult(data.winResult);
        setHumanActions(data.humanActions);
      } else if (event.type === 'BANTER') {
        const { botId, text } = event.data;
        setBotBanters(prev => ({ ...prev, [botId]: text }));

        if (banterTimers.current[botId]) {
          clearTimeout(banterTimers.current[botId]);
        }
        banterTimers.current[botId] = setTimeout(() => {
          setBotBanters(prev => {
            const next = { ...prev };
            delete next[botId];
            return next;
          });
        }, 3500);
      }
    });

    setGameManager(manager);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartGame = () => {
    if (!gameManager) return;
    soundManager.playClick();
    gameManager.startMatch();
    setGameStarted(true);
  };

  const handlePlayerDiscard = (cardId: string) => {
    if (!gameManager) return;
    gameManager.executeDiscard('player_0', cardId);
  };

  const handlePlayerAction = (action: ActionType, chiOption?: ChiOption) => {
    if (!gameManager) return;
    gameManager.handleHumanAction(action, chiOption);
  };

  const handleNextRound = () => {
    if (!gameManager) return;
    soundManager.playClick();
    gameManager.startRound();
  };

  const handleRestartMatch = () => {
    if (!gameManager) return;
    soundManager.playClick();
    gameManager.startMatch();
  };

  const handleUpdateConfig = (newConfig: GameConfig) => {
    setConfig(newConfig);
    gameManager?.updateConfig(newConfig);
  };

  return (
    <div className="app-root">
      {!gameStarted ? (
        <div className="start-screen-wrapper">
          <div className="start-hero-card">
            <div className="start-brand-badge">🎴 传统国粹 · 竞技字牌</div>
            <h1 className="start-title">赛博字牌 · 雀圣风云</h1>
            <p className="start-subtitle">
              还原地道跑胡子玩法，集成高智能算牌 AI 与方言互动
            </p>

            <div className="start-settings-panel">
              <div className="start-setting-row">
                <span className="setting-label">🤖 对手水平:</span>
                <div className="start-btn-group">
                  <button
                    className={`btn-diff-select ${config.botDifficulty === BotDifficulty.EASY ? 'active' : ''}`}
                    onClick={() => setConfig({ ...config, botDifficulty: BotDifficulty.EASY })}
                  >
                    休闲菜鸟
                  </button>
                  <button
                    className={`btn-diff-select ${config.botDifficulty === BotDifficulty.MEDIUM ? 'active' : ''}`}
                    onClick={() => setConfig({ ...config, botDifficulty: BotDifficulty.MEDIUM })}
                  >
                    市井老手
                  </button>
                  <button
                    className={`btn-diff-select ${config.botDifficulty === BotDifficulty.HARD ? 'active' : ''}`}
                    onClick={() => setConfig({ ...config, botDifficulty: BotDifficulty.HARD })}
                  >
                    雀圣大师
                  </button>
                </div>
              </div>

              <div className="start-setting-row">
                <span className="setting-label">🏁 比赛局数:</span>
                <div className="start-btn-group">
                  {[4, 8, 12].map(rounds => (
                    <button
                      key={rounds}
                      className={`btn-diff-select ${config.totalRounds === rounds ? 'active' : ''}`}
                      onClick={() => setConfig({ ...config, totalRounds: rounds })}
                    >
                      {rounds} 局
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="start-actions-row">
              <button className="btn-enter-game pulse-hover" onClick={handleStartGame}>
                🀄 立即开始对局
              </button>
            </div>

            <div className="start-footer-hints">
              <span>80张经典字牌</span> · <span>15胡息起胡</span> · <span>提/跑/坎/碰/吃</span> · <span>名堂加番</span>
            </div>
          </div>
        </div>
      ) : (
        gameManager && currentTurnPlayer && (
          <GameBoard
            gameState={gameState}
            players={players}
            publicCard={publicCard}
            isPublicCardDrawn={isPublicCardDrawn}
            currentTurnPlayer={currentTurnPlayer}
            dealerPlayer={dealerPlayer || players[0]}
            deckSize={deckSize}
            currentRound={currentRound}
            totalRounds={totalRounds}
            winResult={winResult}
            humanActions={humanActions}
            botBanters={botBanters}
            onPlayerDiscard={handlePlayerDiscard}
            onPlayerAction={handlePlayerAction}
            onNextRound={handleNextRound}
            onRestartMatch={handleRestartMatch}
            gameConfig={config}
            onUpdateConfig={handleUpdateConfig}
          />
        )
      )}
    </div>
  );
};

export default App;

